import React, { useEffect, useMemo, useRef, useState } from "react";

const STANDARD_ACTIONS = [
  { key: "view", label: "Lihat" },
  { key: "create", label: "Tambah" },
  { key: "edit", label: "Ubah" },
  { key: "process", label: "Konfirmasi / Proses" },
  { key: "delete", label: "Hapus" },
];

const MODULE_LABELS = {
  dashboard: "Dashboard",
  pos: "Kasir / POS",
  sales: "Penjualan",
  purchases: "Pembelian",
  products: "Produk",
  "product-recipes": "Resep Produk",
  productions: "Produksi",
  materials: "Material",
  "stock-opnames": "Stok Opname",
  "stock-reports": "Laporan Stok",
  "stock-transfers": "Transfer Stok",
  "seller-stocks": "Stok Seller",
  "cash-bank": "Kas & Bank",
  reports: "Laporan",
  customers: "Pelanggan",
  suppliers: "Supplier",
  outlets: "Outlet",
  stores: "Konfigurasi Toko",
  users: "Akun Pengguna",
  roles: "Role",
  permissions: "Permission",
  units: "Satuan",
  region: "Wilayah",
  "ai-assistant": "AI Assistant",
  "activity-logs": "Riwayat Aktivitas",
};

const FALLBACK_SPECIAL = {
  "export-activity-logs": ["activity-logs", "Ekspor riwayat aktivitas"],
  "manage-seller-stocks": ["seller-stocks", "Kelola seluruh seller di outlet"],
  "receive-seller-stocks": ["seller-stocks", "Terima penyerahan sendiri"],
  "return-seller-stocks": ["seller-stocks", "Ajukan pengembalian"],
  "adjust-seller-stocks": ["seller-stocks", "Catat stok rusak / hilang"],
  "configure-seller-stocks": ["seller-stocks", "Atur sumber stok akun"],
  "view-own-sales": ["sales", "Lihat penjualan sendiri"],
  "edit-own-sales": ["sales", "Ubah penjualan sendiri"],
  "process-own-sales": ["sales", "Proses / lunasi penjualan sendiri"],
  "view-sales-reports": ["reports", "Lihat laporan penjualan"],
  "view-store-assets": ["stores", "Lihat aset toko"],
  "view-outlet-reference": ["outlets", "Gunakan sebagai referensi"],
  "view-region-reference": ["region", "Gunakan sebagai referensi"],
  "view-customer-reference": ["customers", "Gunakan sebagai referensi"],
  "view-product-reference": ["products", "Gunakan sebagai referensi"],
  "view-unit-reference": ["units", "Gunakan sebagai referensi"],
  "view-role-reference": ["roles", "Gunakan sebagai referensi"],
  "transfer-cash-bank": ["cash-bank", "Transfer antar kas/bank"],
  "reconcile-cash-bank": ["cash-bank", "Rekonsiliasi saldo"],
  "view-ai-all-outlets": ["ai-assistant", "Akses AI seluruh outlet"],
};

const titleCase = (value) =>
  String(value || "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const normalizePermission = (permission, index) => {
  if (permission?.module && permission?.action) {
    return {
      ...permission,
      module_label: permission.module_label || MODULE_LABELS[permission.module] || titleCase(permission.module),
      action_label: permission.action_label || titleCase(permission.action),
      module_order: Number(permission.module_order ?? 900),
      action_order: Number(permission.action_order ?? 900),
      is_special: Boolean(permission.is_special),
    };
  }

  const slug = String(permission?.slug || "").toLowerCase();
  const special = FALLBACK_SPECIAL[slug];
  if (special) {
    return {
      ...permission,
      module: special[0],
      module_label: MODULE_LABELS[special[0]] || titleCase(special[0]),
      action: slug,
      action_label: special[1],
      module_order: 900,
      action_order: index,
      is_special: true,
    };
  }

  const action = STANDARD_ACTIONS.find(({ key }) => slug.startsWith(`${key}-`))?.key || "view";
  const module = slug.replace(`${action}-`, "") || "lainnya";
  return {
    ...permission,
    module,
    module_label: MODULE_LABELS[module] || titleCase(module),
    action,
    action_label: STANDARD_ACTIONS.find((item) => item.key === action)?.label || titleCase(action),
    module_order: 900,
    action_order: STANDARD_ACTIONS.findIndex((item) => item.key === action),
    is_special: false,
  };
};

const MatrixCheckbox = ({ checked, indeterminate = false, label, disabled, onChange }) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <label className={`permission-check ${disabled ? "is-disabled" : ""}`} title={label}>
      <input
        ref={inputRef}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-label={label}
        onChange={onChange}
      />
      <span aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
          <path d="m5 12 4 4L19 6" />
        </svg>
      </span>
    </label>
  );
};

const PermissionMatrixSkeleton = () => (
  <div className="permission-matrix-skeleton" aria-label="Memuat permission">
    {[...Array(7)].map((_, index) => (
      <div className="permission-matrix-skeleton-row" key={index}>
        <span className="placeholder placeholder-lg" />
        {[...Array(STANDARD_ACTIONS.length)].map((__, cellIndex) => (
          <span className="placeholder placeholder-sm" key={cellIndex} />
        ))}
      </div>
    ))}
  </div>
);

const EmptyPermission = ({ search, enabledOnly }) => (
  <div className="permission-empty">
    <span className="permission-empty-icon">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-4-4" />
      </svg>
    </span>
    <strong>Tidak ada akses yang ditampilkan</strong>
    <small>
      {search
        ? `Tidak ditemukan hasil untuk “${search}”.`
        : enabledOnly
          ? "Role ini belum mempunyai akses aktif pada filter tersebut."
          : "Permission belum tersedia."}
    </small>
  </div>
);

const PermissionMatrix = ({ permissions = [], selectedIds = [], onChange, readOnly = false, loading = false }) => {
  const [search, setSearch] = useState("");
  const [enabledOnly, setEnabledOnly] = useState(false);
  const selectedSet = useMemo(() => new Set(selectedIds.map(Number)), [selectedIds]);

  const modules = useMemo(() => {
    const grouped = new Map();
    permissions.map(normalizePermission).forEach((permission) => {
      const moduleKey = permission.module;
      if (!grouped.has(moduleKey)) {
        grouped.set(moduleKey, {
          key: moduleKey,
          label: permission.module_label,
          order: permission.module_order,
          crud: {},
          special: [],
        });
      }

      const module = grouped.get(moduleKey);
      module.order = Math.min(module.order, permission.module_order);
      if (permission.is_special || !STANDARD_ACTIONS.some(({ key }) => key === permission.action)) {
        module.special.push(permission);
      } else {
        module.crud[permission.action] = permission;
      }
    });

    return [...grouped.values()]
      .map((module) => ({
        ...module,
        special: module.special.sort((a, b) => a.action_order - b.action_order),
      }))
      .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label, "id"));
  }, [permissions]);

  const visibleModules = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return modules.filter((module) => {
      const allPermissions = [...Object.values(module.crud), ...module.special];
      const hasEnabled = allPermissions.some((permission) => selectedSet.has(Number(permission.id)));
      const matchesSearch =
        !keyword ||
        module.label.toLowerCase().includes(keyword) ||
        allPermissions.some((permission) =>
          `${permission.name || ""} ${permission.action_label || ""} ${permission.slug || ""}`
            .toLowerCase()
            .includes(keyword),
        );
      return matchesSearch && (!enabledOnly || hasEnabled);
    });
  }, [enabledOnly, modules, search, selectedSet]);

  const replaceSelected = (nextSet) => onChange?.([...nextSet].sort((a, b) => a - b));

  const togglePermission = (module, permission, checked) => {
    if (readOnly || !permission) return;
    const next = new Set(selectedSet);
    const permissionId = Number(permission.id);

    if (checked) {
      next.add(permissionId);
      if (["create", "edit", "process", "delete"].includes(permission.action) && module.crud.view) {
        next.add(Number(module.crud.view.id));
      }
      if (permission.slug === "process-own-sales") {
        const ownView = module.special.find((item) => item.slug === "view-own-sales");
        if (ownView) next.add(Number(ownView.id));
      }
    } else {
      next.delete(permissionId);
      if (permission.slug === "view-own-sales") {
        const ownProcess = module.special.find((item) => item.slug === "process-own-sales");
        if (ownProcess) next.delete(Number(ownProcess.id));
      }
      if (permission.action === "view") {
        ["create", "edit", "process", "delete"].forEach((action) => {
          if (module.crud[action]) next.delete(Number(module.crud[action].id));
        });
      }
    }

    replaceSelected(next);
  };

  const toggleModule = (module, checked) => {
    if (readOnly) return;
    const next = new Set(selectedSet);
    [...Object.values(module.crud), ...module.special].forEach((permission) => {
      const id = Number(permission.id);
      if (checked) next.add(id);
      else next.delete(id);
    });
    replaceSelected(next);
  };

  const toggleColumn = (action, checked) => {
    if (readOnly) return;
    const next = new Set(selectedSet);
    visibleModules.forEach((module) => {
      const permission = module.crud[action];
      if (!permission) return;
      if (checked) {
        next.add(Number(permission.id));
        if (action !== "view" && module.crud.view) next.add(Number(module.crud.view.id));
      } else {
        next.delete(Number(permission.id));
        if (action === "view") {
          ["create", "edit", "process", "delete"].forEach((dependentAction) => {
            if (module.crud[dependentAction]) next.delete(Number(module.crud[dependentAction].id));
          });
        }
      }
    });
    replaceSelected(next);
  };

  const moduleState = (module) => {
    const ids = [...Object.values(module.crud), ...module.special].map((permission) => Number(permission.id));
    const checkedCount = ids.filter((id) => selectedSet.has(id)).length;
    return { checked: ids.length > 0 && checkedCount === ids.length, indeterminate: checkedCount > 0 && checkedCount < ids.length };
  };

  const columnState = (action) => {
    const ids = visibleModules.map((module) => module.crud[action]).filter(Boolean).map((permission) => Number(permission.id));
    const checkedCount = ids.filter((id) => selectedSet.has(id)).length;
    return { checked: ids.length > 0 && checkedCount === ids.length, indeterminate: checkedCount > 0 && checkedCount < ids.length };
  };

  if (loading) return <PermissionMatrixSkeleton />;

  return (
    <div className="permission-matrix">
      <div className="permission-toolbar">
        <div className="permission-search input-icon">
          <span className="input-icon-addon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-4-4" />
            </svg>
          </span>
          <input
            type="search"
            className="form-control"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Cari modul atau akses..."
            aria-label="Cari modul atau permission"
          />
        </div>
        <label className="permission-active-filter">
          <input type="checkbox" checked={enabledOnly} onChange={(event) => setEnabledOnly(event.target.checked)} />
          <span>Aktif saja</span>
        </label>
      </div>

      {visibleModules.length === 0 ? (
        <EmptyPermission search={search} enabledOnly={enabledOnly} />
      ) : (
        <>
          <div className="permission-table-wrap d-none d-lg-block">
            <table className="permission-table">
              <thead>
                <tr>
                  <th>Modul & akses</th>
                  {STANDARD_ACTIONS.map(({ key, label }) => {
                    const state = columnState(key);
                    return (
                      <th key={key}>
                        <span>{label}</span>
                        <MatrixCheckbox
                          {...state}
                          disabled={readOnly}
                          label={`Pilih semua akses ${label.toLowerCase()} yang tampil`}
                          onChange={(event) => toggleColumn(key, event.target.checked)}
                        />
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {visibleModules.map((module) => {
                  const state = moduleState(module);
                  return (
                    <React.Fragment key={module.key}>
                      <tr>
                        <td>
                          <div className="permission-module-name">
                            <MatrixCheckbox
                              {...state}
                              disabled={readOnly}
                              label={`Pilih seluruh akses ${module.label}`}
                              onChange={(event) => toggleModule(module, event.target.checked)}
                            />
                            <div>
                              <strong>{module.label}</strong>
                              <small>{Object.values(module.crud).length + module.special.length} akses tersedia</small>
                            </div>
                          </div>
                        </td>
                        {STANDARD_ACTIONS.map(({ key, label }) => {
                          const permission = module.crud[key];
                          return (
                            <td key={key}>
                              {permission ? (
                                <MatrixCheckbox
                                  checked={selectedSet.has(Number(permission.id))}
                                  disabled={readOnly}
                                  label={`${label} ${module.label}`}
                                  onChange={(event) => togglePermission(module, permission, event.target.checked)}
                                />
                              ) : (
                                <span className="permission-unavailable" aria-label={`${label} tidak tersedia`}>
                                  —
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                      {module.special.length > 0 && (
                        <tr className="permission-special-row">
                          <td colSpan={STANDARD_ACTIONS.length + 1}>
                            <div className="permission-special-list">
                              <span className="permission-special-title">Akses khusus</span>
                              {module.special.map((permission) => (
                                <label className="permission-special-option" key={permission.id}>
                                  <MatrixCheckbox
                                    checked={selectedSet.has(Number(permission.id))}
                                    disabled={readOnly}
                                    label={`${permission.action_label} pada ${module.label}`}
                                    onChange={(event) => togglePermission(module, permission, event.target.checked)}
                                  />
                                  <span>{permission.action_label || permission.name}</span>
                                </label>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="permission-mobile-list d-lg-none">
            {visibleModules.map((module) => {
              const state = moduleState(module);
              const enabledCount = [...Object.values(module.crud), ...module.special].filter((permission) =>
                selectedSet.has(Number(permission.id)),
              ).length;
              return (
                <details className="permission-mobile-card" key={module.key}>
                  <summary>
                    <div>
                      <strong>{module.label}</strong>
                      <small>{enabledCount} akses aktif</small>
                    </div>
                    <span className="permission-mobile-chevron" aria-hidden="true">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                    </span>
                  </summary>
                  <div className="permission-mobile-body">
                    <label className="permission-mobile-all">
                      <span>Pilih semua akses modul</span>
                      <MatrixCheckbox
                        {...state}
                        disabled={readOnly}
                        label={`Pilih seluruh akses ${module.label}`}
                        onChange={(event) => toggleModule(module, event.target.checked)}
                      />
                    </label>
                    <div className="permission-mobile-grid">
                      {STANDARD_ACTIONS.map(({ key, label }) => {
                        const permission = module.crud[key];
                        if (!permission) return null;
                        return (
                          <label className="permission-mobile-option" key={key}>
                            <span>{label}</span>
                            <MatrixCheckbox
                              checked={selectedSet.has(Number(permission.id))}
                              disabled={readOnly}
                              label={`${label} ${module.label}`}
                              onChange={(event) => togglePermission(module, permission, event.target.checked)}
                            />
                          </label>
                        );
                      })}
                    </div>
                    {module.special.length > 0 && (
                      <div className="permission-mobile-special">
                        <span>Akses khusus</span>
                        {module.special.map((permission) => (
                          <label className="permission-mobile-option" key={permission.id}>
                            <span>{permission.action_label || permission.name}</span>
                            <MatrixCheckbox
                              checked={selectedSet.has(Number(permission.id))}
                              disabled={readOnly}
                              label={`${permission.action_label} pada ${module.label}`}
                              onChange={(event) => togglePermission(module, permission, event.target.checked)}
                            />
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </details>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default PermissionMatrix;
